import { UtilService } from "../services/UtilService";

//Utilizarlo con un iframe
export const buildPrintableSrcDoc = (ticketHtml: string): string => {
    const style = `
        <style>
        @page { size: A4; margin: 12mm; }
        @media print {
            html, body { height: auto; margin: 0; padding: 0; background: #fff !important; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { width: 210mm; min-height: 297mm; box-sizing: border-box; overflow: hidden; }
            .page-break { page-break-before: always; }
            .no-break { break-inside: avoid; page-break-inside: avoid; }
            .print-root { transform-origin: top left; transform: scale(0.95); } /* opcional */
            .no-print { display: none !important; }
        }
        </style>
    `;

    const script = `
        <script>
        // Escucha mensaje del parent y ejecuta window.print() dentro del iframe
        window.addEventListener('message', function(e) {
            try {
            if (e && e.data === 'print') {
                var ensureFonts = (document.fonts && document.fonts.ready)
                ? document.fonts.ready
                : Promise.resolve();

                ensureFonts.then(function () {
                // Si querés forzar que se imprima el body, asegurate que el contenido principal esté en el body
                // y que no haya elementos "no-print".
                window.focus();
                window.print();
                });
            }
            } catch (err) {
            console.error('Print inside iframe failed:', err);
            }
        }, false);
        </script>
    `;

    // Inyectar <style> y <script> antes del cierre de </head> y </body> si existen
    let html = ticketHtml;

    // Si el HTML trae </head>, metemos <style> ahí para aplicar antes del render
    if (/<\/head>/i.test(html)) {
        html = html.replace(/<\/head>/i, style + '</head>');
    } else {
        // Si no hay <head>, agregamos estilos al inicio del documento
        html = style + html;
    }

    // Inyectar el <script> antes de </body> para asegurar que está en el DOM
    if (/<\/body>/i.test(html)) {
        html = html.replace(/<\/body>/i, script + '</body>');
    } else {
        html = html + script;
    }

    return html;
}


// Recorre TODOS los elementos del <body> que tengan data-card-id
// y para cada uno inserta/actualiza <img class="cupon__logo"> dentro de su contenedor.

export const attachIssuerLogo = (ticketHtml: string, t: any): string => {
  if (!ticketHtml || typeof ticketHtml !== 'string') return ticketHtml;

  const parser = new DOMParser();
  const doc = parser.parseFromString(ticketHtml, 'text/html');
  const body = doc.body ?? doc.documentElement;

  // --- Helpers ---------------------------------------------------------------
  // Obtiene el contenedor donde insertar el logo:
  // - si no existe: crea un wrapper .issuer-container y envuelve al elemento
  const getOrCreateIssuerContainer = (el: Element): HTMLElement => {
    if (el instanceof HTMLElement && el.classList.contains('issuer-container')) {
      return el;
    }
    const closest = el.closest('.issuer-container');
    if (closest && closest instanceof HTMLElement) {
      return closest;
    }
    const wrapper = doc.createElement('div');
    wrapper.className = 'issuer-container';
    el.parentNode?.insertBefore(wrapper, el);
    wrapper.appendChild(el);
    return wrapper;
  };

  // Inserta/actualiza la imagen del logo dentro del contenedor
  const upsertLogoImg = (container: HTMLElement, src: string) => {
    let imgEl = container.querySelector('img.cupon__logo') as HTMLImageElement | null;
    if (!imgEl) {
      imgEl = doc.createElement('img');
      imgEl.className = 'cupon__logo';
      container.insertBefore(imgEl, container.firstChild);
    }
    imgEl.setAttribute('alt', 'Logo');
    imgEl.setAttribute('loading', 'lazy');
    imgEl.setAttribute('decoding', 'async');
    imgEl.setAttribute('width', '60');
    imgEl.setAttribute('height', '30');

    imgEl.setAttribute('data-logo-source', src);
    imgEl.src = src;
    imgEl.onerror = () => { imgEl!.src = '/cards/default.png'; };
  };

  // Resuelve la imagen desde UtilService (con fallback)
  const resolveLogoSrc = (issuer: string): string => {
    try {
      const data = UtilService.getIssuerGridValueInt(issuer, 'issuer', t) as { value?: string; image?: string };
      const src = (data?.image ?? data?.value ?? '').toString().trim();
      return src || '/cards/default.png';
    } catch {
      return '/cards/default.png';
    }
  };
  // Marcador para no duplicar trabajo sobre el mismo elemento en una sola pasada
  const LOGO_BOUND_ATTR = 'data-logo-bound';

  // Seleccionamos únicamente los nodos que poseen data-card-id
  const nodesWithCardId = Array.from(body.querySelectorAll('[data-card-id]'));

  for (const el of nodesWithCardId) {
    const hEl = el as HTMLElement;

    // Evitar reprocesar si ya lo marcamos
    if (hEl.getAttribute(LOGO_BOUND_ATTR) === 'true') continue;

    // Leer issuer desde data-card-id
    const rawAttr = el.getAttribute('data-card-id') ?? '';
    const rawDataset = (hEl.dataset?.cardId ?? '');
    const issuer = (rawAttr || rawDataset).trim();
    if (!issuer) continue;

    // Resolver contenedor y logo
    const container = getOrCreateIssuerContainer(el);
    const src = resolveLogoSrc(issuer);
    upsertLogoImg(container, src);

    // Marcar como procesado este elemento
    hEl.setAttribute(LOGO_BOUND_ATTR, 'true');
  }

  const hasHtmlTag = !!doc.documentElement?.outerHTML;
  return hasHtmlTag ? doc.documentElement.outerHTML : doc.body.innerHTML;
}

// helpers couponWallet logo
export function attachWalletLogo(
  ticketHtml: string,
  t: (k: string) => string,
): string {
  const parser = new DOMParser();
    const doc = parser.parseFromString(ticketHtml, 'text/html');

    const walletSourceEl =
        doc.querySelector('[data-wallet-id]') ||
        doc.querySelector('.wallet-container');


  const rawWallet =
    walletSourceEl?.getAttribute('data-wallet-id') ??
    (walletSourceEl as HTMLElement | null)?.dataset?.walletId ??
    '';

  const wallet = String(rawWallet || '').trim();
  if (!wallet) {
    return ticketHtml;
  }

  const i18nKey = `issuer-${wallet}`; 
  const src = UtilService.getImageTag(t(i18nKey));

  let imgEl = doc.querySelector('img.wallet__logo') as HTMLImageElement | null;

  if (!imgEl) {
    const container = doc.querySelector('.wallet-container');
    imgEl = doc.createElement('img');
    imgEl.className = 'wallet__logo';
    container?.insertBefore(imgEl, container.firstChild);
  }

  imgEl.setAttribute('alt', `Logo`);
  imgEl.setAttribute('loading', 'lazy');
  imgEl.setAttribute('decoding', 'async');
  imgEl.setAttribute('width', '60');
  imgEl.setAttribute('height', '30');

  imgEl.src = src;

  const hasHtmlTag = !!doc.documentElement?.outerHTML;
  return hasHtmlTag ? doc.documentElement.outerHTML : doc.body.innerHTML;
}
